import { useState } from 'react';
import { CheckCircle, Award, Users, Heart, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import AppointmentModal from '../components/AppointmentModal';

const ABOUT_IMAGE = 'https://images.pexels.com/photos/35800079/pexels-photo-35800079.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const VALUES = [
  {
    icon: Heart,
    title: 'Personal Care',
    description: 'We take personal responsibility for every case. From impression to final fit, you work directly with the professional who creates your dentures.'
  },
  {
    icon: Award,
    title: 'Quality Materials',
    description: 'We use advanced materials, modern fabrication techniques, and high-quality acrylic systems to ensure durability, comfort, and natural appearance.'
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'We stand behind our work with honest assessments, transparent pricing, and ongoing support for all our patients.'
  },
  {
    icon: Users,
    title: 'Community Focus',
    description: 'For decades, we have proudly served Coos County and surrounding Oregon communities with dedication and care.'
  },
];

const HIGHLIGHTS = [
  "Oregon's longest standing Denturist practice",
  "Specializing exclusively in removable prosthodontics",
  "Advanced materials and modern techniques",
  "Direct care from licensed professionals",
  "Personalized attention for every patient",
  "Ongoing maintenance and support",
];

export default function AboutPage() {
  const [showAppointment, setShowAppointment] = useState(false);

  return (
    <div className="pt-24 lg:pt-32" data-testid="about-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Award className="w-4 h-4" />
                Established Practice
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Oregon's Longest Standing Denturist Practice
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                For decades, our practice has served Coos County and surrounding Oregon communities with integrity, precision, and personal care.
              </p>
              <p className="mt-4 text-slate-600">
                We are a licensed Denturist practice specializing exclusively in removable prosthodontics and oral appliances.
              </p>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-orange-100 rounded-3xl transform -rotate-3" />
              <img
                src={ABOUT_IMAGE}
                alt="Dental technician at work"
                className="relative rounded-3xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Mission
            </h2>
            <p className="mt-6 text-2xl text-slate-700 leading-relaxed font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
              "Restore smiles. Restore confidence. Restore oral function."
            </p>
            <p className="mt-8 text-lg text-slate-600 leading-relaxed">
              We take personal responsibility for every case. From impression to final fit, you work directly with the professional who creates your dentures. We stand behind our work and are committed to your satisfaction.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-slate-50" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our Values
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              The principles that guide our practice
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((value, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {value.title}
                </h3>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-24 bg-slate-900 text-white" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Why Patients Trust Us
              </h2>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed">
                As Oregon's longest-standing denturist practice, our longevity reflects our commitment to quality, ethics, and patient satisfaction.
              </p>
              <ul className="mt-10 space-y-4">
                {HIGHLIGHTS.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <span className="text-slate-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-800 rounded-3xl p-8 lg:p-12">
              <h3 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                What Sets Us Apart
              </h3>
              <div className="space-y-6">
                <div className="border-l-4 border-orange-600 pl-6">
                  <h4 className="font-medium text-lg">Direct Fabrication</h4>
                  <p className="mt-2 text-slate-400 text-sm">
                    No third-party labs. We design and create your dentures in-house for superior quality control.
                  </p>
                </div>
                <div className="border-l-4 border-orange-600 pl-6">
                  <h4 className="font-medium text-lg">Personalized Attention</h4>
                  <p className="mt-2 text-slate-400 text-sm">
                    You work directly with the professional who creates your dentures throughout the entire process.
                  </p>
                </div>
                <div className="border-l-4 border-orange-600 pl-6">
                  <h4 className="font-medium text-lg">Ongoing Support</h4>
                  <p className="mt-2 text-slate-400 text-sm">
                    We provide maintenance, adjustments, and repairs to keep your dentures comfortable for years.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-orange-600" data-testid="about-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Experience the Difference
          </h2>
          <p className="mt-4 text-xl text-orange-100">
            Schedule a consultation and see why patients across Oregon trust us with their smiles.
          </p>
          <Button
            onClick={() => setShowAppointment(true)}
            className="mt-10 bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg shadow-xl transition-all hover:scale-105"
            data-testid="about-schedule-btn"
          >
            Schedule Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </div>
  );
}
